'use client'

import { useEffect } from 'react'
import { trackException } from '@/src/lib/appInsights'
import { logger } from '@/src/lib/logger'

/**
 * ErrorState — componente reutilizável para feedback de erro.
 *
 * Variantes:
 * - `fullScreen` (default): card centralizado, ocupa a tela. Bom para falha de página inteira.
 * - `inline`: dentro de uma seção/card, mantém o resto da UI.
 *
 * Tracking automático em App Insights quando recebe um Error real (não string).
 *
 * Task #195 / F13.6 — padronização visual de estados de erro.
 */

interface ErrorStateProps {
  /** Erro original (Error obj reporta para App Insights; string só exibe) */
  error?: Error | string | null
  /** Callback do botão "Tentar novamente". Se omitido, esconde o botão. */
  onRetry?: () => void
  title?: string
  message?: string
  variant?: 'fullScreen' | 'inline'
  /** Identificador da origem do erro (vira propriedade no trackException) */
  source?: string
  className?: string
}

export function ErrorState({
  error,
  onRetry,
  title = 'Algo deu errado',
  message,
  variant = 'fullScreen',
  source = 'ErrorState',
  className = '',
}: ErrorStateProps) {
  // Tracking de erro real para App Insights (apenas se for Error de verdade)
  useEffect(() => {
    if (error instanceof Error) {
      trackException(error, {
        source,
        url: typeof window !== 'undefined' ? window.location.href : '',
      })
      logger.error(`ErrorState: ${source}`, error)
    }
  }, [error, source])

  const finalMessage =
    message ??
    (typeof error === 'string'
      ? error
      : 'Ocorreu um erro inesperado. Por favor, tente novamente em alguns instantes.')

  const containerClass =
    variant === 'fullScreen'
      ? 'flex min-h-[60vh] items-center justify-center p-6'
      : 'flex items-center justify-center py-6'

  return (
    <div className={`${containerClass} ${className}`} role="alert" aria-live="polite">
      <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
        <div className="mb-3 flex justify-center">
          <i
            className="bi bi-exclamation-triangle-fill text-3xl text-red-600"
            aria-hidden="true"
          />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-red-700">{title}</h2>
        <p className="mb-5 text-sm text-gray-600">{finalMessage}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            Tentar novamente
          </button>
        )}
      </div>
    </div>
  )
}

export default ErrorState
